import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import LazyImage from "./lazyImage";
import { BackIcon, StoreIcon, UserIcon } from "../shared/icons";
import { Stores } from "../dataLists/index";
import { LocalizedLink } from "../i18n/LocaleRouting";
import { regionLabel } from "../i18n/display";

type Props = {
  props: { src: string; prefecture: string };
};

const HOTSHOP_LOGOS = [
  "/icon/hotshoplogo1.png",
  "/icon/hotshoplogo2.png",
  "/icon/hotshoplogo3.png",
  "/icon/hotshoplogo4.png",
  "/icon/hotshoplogo5.png",
  "/icon/hotshoplogo6.png",
  "/icon/hotshoplogo7.png",
  "/icon/hotshoplogo8.png",
];

function shuffleLogos(logos: string[]) {
  const shuffled = [...logos];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/** Prefetch counts once so every CityFrame card can look up instantly. */
const storeCountByPrefecture = (() => {
  const counts = new Map<string, number>();
  for (const store of Stores) {
    const key = store.prefecture?.toLowerCase();
    if (!key) continue;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
})();

const PLAYERS_COUNT_BY_AREA: Record<string, string> = {
  osaka: "2.2M",
  tokyo: "2.2M",
  nagoya: "2.2M",
  fukuoka: "2.2M",
  okinawa: "2.2M",
  hongkong: "50K",
  taiwan: "200K",
  bangkok: "100K",
  chiangmai: "100K",
  seoul: "80K",
  busan: "80K",
  malaysia: "100K",
  kualalumpur: "100K",
  sydney: "120K",
  singapore: "40K",
};

const CityFrame: React.FC<Props> = ({ props }) => {
  const { t } = useTranslation();
  const { src, prefecture } = props;
  const displayName = regionLabel(t, prefecture);
  const normalizedArea = prefecture.toLowerCase().replace(/\s+/g, "");
  const shopsCount = storeCountByPrefecture.get(prefecture.toLowerCase()) ?? 0;
  const [shuffledLogos] = useState(() => shuffleLogos(HOTSHOP_LOGOS));
  const playersCount = PLAYERS_COUNT_BY_AREA[normalizedArea] ?? "0K";

  return (
    <LocalizedLink
      target="_top"
      to={`/regionalstore/${prefecture.toLowerCase().replace(/ /g, "-")}`}
      className="w-full h-full rounded-2xl overflow-hidden shadow-md hover:shadow-xl transform transition-transform duration-500 ease-in-out hover:-translate-y-4 cursor-pointer"
    >
      <section
        className="relative hotshop-logos-marquee h-[300px] md:h-[340px] w-full bg-white"
      >
        <div className="absolute rounded-l-full top-[51%] md:top-[57%] right-0 z-10 py-1 bg-[#000]/5 backdrop-blur-[4px]">
          <span className="capitalize text-[20px] font-bold text-[#fff]/90 px-4">
            {displayName}
          </span>
        </div>

        <div className="w-full h-full flex flex-col justify-center items-center">
          <div className="w-full md:h-[calc(100%-32%)] h-[calc(100%-36%)] flex justify-center items-center">
            <LazyImage
              src={src || ""}
              alt={displayName}
              className="w-full h-full object-cover brightness-[100%] contrast-100 saturate-[120%]"
            />
          </div>
          <div className="w-full md:h-[32%] h-[36%] flex flex-col md:pt-[2%] pt-[1%] px-6">
            <div className="w-full flex justify-center items-center h-full">
              <div className="hotshop-logos-marquee w-[96%] md:h-12 h-10 overflow-hidden rounded-lg bg-black/0 flex items-center px-2">
                <div className="hotshop-logos-marquee-track flex shrink-0 items-center gap-x-[4px]">
                  {[...shuffledLogos, ...shuffledLogos].map((logo, index) => (
                    <img
                      key={`${logo}-${index}`}
                      src={logo}
                      alt=""
                      className="h-8 shrink-0 object-cover"
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="w-full h-full flex justify-between items-center md:mt-[8px] mt-[6px] border-t-[1px] border-t-[#000]/10">
              <div className="flex items-center gap-x-2 flex-1">
                <div className="flex items-center gap-x-2 border-r-[1px] border-r-[#000]/10 pr-2">
                  <StoreIcon color="#F05A2A" size={28} />
                  <p className="flex flex-col">
                    <b className="text-xs text-[#000]/90 leading-none">
                      {shopsCount}
                    </b>
                    <span className="text-[10px] text-[#000]/40 leading-none">
                      {t("cityCard.shops")}
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-x-2 border-r-[1px] border-r-[#000]/10 pr-2">
                  <UserIcon color="#00000070" size={26} />
                  <p className="flex flex-col">
                    <b className="text-xs text-[#000]/90 leading-none">
                      {playersCount}
                    </b>
                    <span className="text-[10px] text-[#000]/40 leading-none">
                      {t("cityCard.players")}
                    </span>
                  </p>
                </div>
              </div>
              <button className="flex items-center gap-x-1 bg-[#ff7040] hover:bg-[#F05A2A] text-white pl-[6px] md:py-[3px] py-[2px] text-xs font-medium rounded-md">
                {t("cityCard.explore")}
                <span className="scale-[0.5] rotate-180">
                  <BackIcon color="#fff" strokeWidth={3} />
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>
    </LocalizedLink>
  );
};

export default CityFrame;
